import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { authAPI } from "./services/api";
import UploadLecture from "./pages/UploadLecture";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import CoursesPage from "./pages/CoursesPage";
import AddExamQuestions from "./pages/AddExamQuestions";
import CourseDetails from "./pages/CourseDetails";
import ExamsPage from "./pages/ExamsPage";
import EditExam from "./pages/EditExam";
import ExamDetails from "./pages/ExamDetails";

import LessonDetails from "./pages/LessonDetails";
import AddCourse from "./pages/AddCourse";
import EditLesson from "./pages/EditLesson";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Layout from "./components/Layout";
import "./App.css";
import ActivityLogs from "./pages/ActivityLogs";
import LegalPages from "./pages/LegalPages";
import Students from "./pages/Students";
import ViewLectures from "./pages/ViewLectures";
import Support from "./pages/Support";
import AdminTeachers from "./pages/AdminTeachers";
import ManageBanners from "./pages/ManageBanners";
import AICredits from "./pages/AICredits";
import AdminAICredits from "./pages/AdminAICredits";
import ActivationEmailsAdmin from "./pages/ActivationEmailsAdmin";
import Pricing from "./pages/Pricing";

import PendingApproval from "./pages/PendingApproval";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userStatus, setUserStatus] = useState("active"); // Default active

  useEffect(() => {
    const checkAuth = async () => {
      const authToken = localStorage.getItem("authToken");
      const userId = localStorage.getItem("userId");

      if (!authToken || !userId) {
        setIsAuthenticated(false);
        // setUserRole(null);
        setLoading(false);
        return;
      }

      try {
        // ✅ التحقق من `authToken` ومزامنة المستخدم مع MySQL
        const response = await authAPI.syncUser();
        const userData = response.data.user;

        if (!userData || !userData.id) {
          throw new Error("❌ فشل التحقق من المستخدم.");
        }

        // تخزين ID المستخدم ودوره من MySQL
        localStorage.setItem("dbUserId", userData.id);
        localStorage.setItem("userRole", userData.role || "teacher");
        localStorage.setItem("userStatus", userData.status || "active");
        localStorage.setItem("userName", userData.full_name || "المعلم");

        setUserStatus(userData.status || "active");
        setIsAuthenticated(true);
      } catch (error) {
        // ✅ إذا كان الحساب محذوفاً من قِبَل الأدمن
        if (error.response?.status === 403 && error.response?.data?.code === 'ACCOUNT_DELETED') {
          alert("⛔ تم حذف حسابك من قِبَل الإدارة. للاستفسار تواصل معنا.");
        }
        localStorage.removeItem("authToken");
        localStorage.removeItem("userId");
        localStorage.removeItem("dbUserId");
        localStorage.removeItem("userRole");
        localStorage.removeItem("userStatus");
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (loading) {
    return <p className="loading-message">⏳ جارٍ تحميل البيانات...</p>;
  }

  // Route Guard Helper
  const ProtectedRoute = ({ children }) => {
    if (!isAuthenticated) return <Navigate to="/" />;
    if (userStatus === 'pending' || userStatus === 'disabled') return <Navigate to="/pending" />;
    return children;
  };

  // Admin Route Guard (admin only)
  const AdminRoute = ({ children }) => {
    const role = localStorage.getItem("userRole");
    if (!isAuthenticated) return <Navigate to="/" />;
    if (role !== "admin") {
      alert("❌ ليس لديك صلاحية للوصول إلى هذه الصفحة.");
      return <Navigate to="/dashboard" />;
    }
    return children;
  };

  // Admin + Owner Route Guard
  const AdminOwnerRoute = ({ children }) => {
    const role = localStorage.getItem("userRole");
    if (!isAuthenticated) return <Navigate to="/" />;
    if (role !== "admin" && role !== "owner") return <Navigate to="/dashboard" />;
    return children;
  };

  return (
    <Router>
      <ToastContainer position="top-left" autoClose={3000} />
      <Routes>
        {/* ✅ إعادة التوجيه تلقائيًا بعد تسجيل الدخول */}
        <Route path="/" element={isAuthenticated ? (userStatus === 'pending' ? <Navigate to="/pending" /> : <Navigate to="/dashboard" />) : <Login />} />
        {/* ✅ مسار Login الصريح لمنع الصفحة البيضاء عند تسجيل الخروج */}
        <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" /> : <Login />} />

        <Route path="/register" element={isAuthenticated ? <Navigate to="/dashboard" /> : <Register />} />
        <Route path="/forgot-password" element={isAuthenticated ? <Navigate to="/dashboard" /> : <ForgotPassword />} />

        {/* صفحة الانتظار */}
        <Route path="/pending" element={isAuthenticated ? (userStatus === 'active' ? <Navigate to="/dashboard" /> : <PendingApproval />) : <Navigate to="/" />} />

        {/* ✅ المسارات المحمية داخل Layout */}
        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/upload/:courseId?" element={<UploadLecture />} />
          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/add-exam-questions" element={<AddExamQuestions />} />
          <Route path="/courses/:courseId" element={<CourseDetails />} />
          <Route path="/Courses/:courseId/Exams/:examId" element={<ExamDetails />} />
          <Route path="/exams/edit/:examId" element={<EditExam />} />
          <Route path="/courses/:courseId/lessons/:lessonId" element={<LessonDetails />} />
          <Route path="/edit-exam/:courseId/:examId" element={<EditExam />} />
          <Route path="/add-course" element={<AddCourse />} />
          <Route path="/edit-lesson/:courseId/:lessonId" element={<EditLesson />} />
          <Route path="/activity-logs" element={<ActivityLogs />} />
          <Route path="/legalpages" element={<LegalPages />} />
          <Route path="/students" element={<Students />} />
          <Route path="/ViewLectures" element={<ViewLectures />} />
          <Route path="/exams" element={<ExamsPage />} />

          <Route path="/view-lectures" element={<ViewLectures />} />
          <Route path="/support" element={<Support />} />
          
          {/* ✅ Teacher: AI Credits */}
          <Route path="/ai-credits" element={<AICredits />} />
          <Route path="/pricing" element={<Pricing />} />

          {/* ✅ Admin Only Routes */}
          <Route path="/admin/teachers" element={<AdminRoute><AdminTeachers /></AdminRoute>} />
          <Route path="/manage-banners" element={<AdminRoute><ManageBanners /></AdminRoute>} />
          <Route path="/admin/banners" element={<AdminRoute><ManageBanners /></AdminRoute>} />
          <Route path="/admin/ai-credits" element={<AdminRoute><AdminAICredits /></AdminRoute>} />
          <Route path="/admin/activation-emails" element={<AdminOwnerRoute><ActivationEmailsAdmin /></AdminOwnerRoute>} />
        </Route>
      </Routes>

    </Router>
  );
}

export default App;
